// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import {
  Icon,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import APIError from "components/ApiError";
import DeleteConfirmationDialog from "components/Dialog/DeleteDialog";
import MDTypography from "components/MDTypography";
import { useEffect, useState } from "react";
import paymentAPIService from "services/paymentAPI-service";
import AddCostPerClass from "./addCost";

function CostPerClass() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [processing, setProcessing] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addCostDialogOpen, setAddCostDialogOpen] = useState(false);

  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [deleteItem, setDeleteItem] = useState("");

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarColor, setSnackBarColor] = useState("success");
  const [snackBarIcon, setSnackBarIcon] = useState("");
  const [snackBarTitle, setSnackBarTitle] = useState("");
  const [snackBarMessage, setSnackBarMessage] = useState("");

  const openSnackBar = () => setSnackBarOpen(true);
  const closeSnackBar = () => setSnackBarOpen(false);

  const columns = ["Cost", "Note", "Action"];

  useEffect(() => {
    fetchAllCostsPerClass();
  }, []);

  const fetchAllCostsPerClass = async () => {
    setProcessing(true);
    setApiError(false);

    try {
      const costPerClassList = await paymentAPIService.getAllCostPerClass();
      setTotal(Array.from(costPerClassList.data).length);
      setRows(costPerClassList.data);
      setProcessing(false);
      setApiError(false);
    } catch (error) {
      setApiError(true);
      setProcessing(false);
    }
  };

  const handleAddCost = () => {
    setAddCostDialogOpen(true);
  };

  const handleAddCostClose = (response) => {
    if (response === "Close") {
      setAddCostDialogOpen(false);
    } else {
      setSnackBarColor("success");
      setSnackBarIcon("check");
      setSnackBarTitle("Success");
      setSnackBarMessage("Cost Per Class has been added.");
      openSnackBar();
      fetchAllCostsPerClass();
      setAddCostDialogOpen(false);
    }
  };

  const handleDeleteConfirmation = (id) => {
    setDialogMessage("Are you sure you want to remove this cost record?");
    setDeleteItem(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = async (response) => {
    if (response === "No") {
      setDeleteItem(null);
      setDeleteDialogOpen(false);
    } else {
      setDeleteProcessing(true);

      try {
        const deleteResponse = await paymentAPIService.deleteCostPerClass(
          deleteItem
        );
        setSnackBarColor("success");
        setSnackBarIcon("check");
        setSnackBarTitle("Success");
        setSnackBarMessage("Cost has been deleted.");
        openSnackBar();

        fetchAllCostsPerClass();
        setDeleteProcessing(false);
        setDeleteItem(null);
        setDeleteDialogOpen(false);
      } catch (error) {
        setSnackBarColor("error");
        setSnackBarIcon("warning");
        setSnackBarTitle("Error");
        setSnackBarMessage("Failed to remove the Cost Record.");
        openSnackBar();
        setDeleteProcessing(false);
        setDeleteDialogOpen(false);
      }
    }
  };

  const renderSnackBar = (
    <MDSnackbar
      color={snackBarColor}
      icon={snackBarIcon}
      title={snackBarTitle}
      content={snackBarMessage}
      dateTime=""
      open={snackBarOpen}
      onClose={closeSnackBar}
      close={closeSnackBar}
      bgWhite
    />
  );

  const renderCostDelete = (
    <DeleteConfirmationDialog
      open={deleteDialogOpen}
      onClose={handleDeleteClose}
      message={dialogMessage}
    />
  );

  const renderAddStudentDialog = (
    <AddCostPerClass open={addCostDialogOpen} onClose={handleAddCostClose} />
  );

  return (
    <MDBox pt={6} pb={3}>
      {/* Add Button */}
      <MDBox
        mb={5}
        mt={-5}
        pr={2}
        display="flex"
        justifyContent="end"
        gap={2}
        flexWrap="wrap"
      >
        <MDButton
          variant="contained"
          color="dark"
          type="button"
          onClick={handleAddCost}
        >
          + Add Cost
        </MDButton>
      </MDBox>

      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card>
            {/* Heading */}
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Cost per class
              </MDTypography>
            </MDBox>

            {/* Progress Bar */}
            {(processing || deleteProcessing) && (
              <MDBox pt={2} px={2}>
                <LinearProgress
                  color="info"
                  variant="indeterminate"
                  sx={{ overflow: "hidden" }}
                />
              </MDBox>
            )}

            {/* No Data */}
            {!processing && !apiError && total === 0 && (
              <MDBox pt={3} px={4} pb={5}>
                <MDTypography variant="h7" color="error">
                  No cost record found
                </MDTypography>
              </MDBox>
            )}

            {/* Table */}
            {!processing && total > 0 && (
              <MDBox pt={3} px={1} pb={5}>
                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                  <Table>
                    {/* Header Columns */}
                    <TableHead>
                      <TableRow>
                        {columns.map((column, i) => (
                          <TableCell key={i}>{column}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    {/* Body Contents */}
                    <TableBody>
                      {rows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <MDTypography variant="h7" fontWeight="regular">
                              $ {row.cost}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant="h7" fontWeight="regular">
                              {row.note}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <Icon
                              sx={{ cursor: "pointer", fontWeight: "regular" }}
                              fontSize="medium"
                              color="error"
                              onClick={() => {
                                handleDeleteConfirmation(row.id);
                              }}
                            >
                              delete_forever
                            </Icon>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            )}

            {/* Api Error */}
            {!processing && apiError && <APIError />}
          </Card>
        </Grid>
      </Grid>
      {renderSnackBar}
      {renderCostDelete}
      {renderAddStudentDialog}
    </MDBox>
  );
}

export default CostPerClass;
